import React, { useEffect, useState } from 'react'
import { API_BASE_URL } from '../data'
import { Event, User } from '../data/types'
import { CheckIcon, XIcon } from '../icons'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { sortByName } from '../utils/sorting'
import { formatDateOnly } from '../utils/dates'

type Props = {
    isOpen: boolean
    onClose: () => void
    user: User | null
}

export const EventReallocationAccessModal = (props: Props) => {
    const { isOpen, onClose, user } = props

    const [allEvents, setAllEvents] = useState<Event[]>([])
    const [accessibleEventIds, setAccessibleEventIds] = useState<number[]>([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Fetch all events and user's current access when modal opens
    useEffect(() => {
        if (!isOpen || !user) return

        const fetchData = async () => {
            setLoading(true)
            setError(null)

            try {
                // Fetch all events
                const eventsResponse = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (eventsResponse.ok) {
                    const result = await eventsResponse.json()
                    let eventData = result.value || result
                    if (Array.isArray(eventData) && eventData.length > 0 && Array.isArray(eventData[0])) {
                        eventData = eventData[0]
                    }
                    setAllEvents(eventData)
                }

                // Fetch user's current event access
                const accessResponse = await fetch(`${API_BASE_URL}/event-reallocation/user/${user.id}/events`, { credentials: 'include' })
                if (accessResponse.ok) {
                    const accessEvents = await accessResponse.json()
                    setAccessibleEventIds(accessEvents.map((e: Event) => e.id))
                }
            } catch (err) {
                setError('Failed to load data')
                console.error('Error fetching data:', err)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [isOpen, user])

    const handleToggleEvent = (eventId: number) => {
        setAccessibleEventIds(prev => {
            if (prev.includes(eventId)) {
                return prev.filter(id => id !== eventId)
            } else {
                return [...prev, eventId]
            }
        })
    }

    const handleSelectAll = () => {
        setAccessibleEventIds(allEvents.map(e => e.id))
    }

    const handleSelectNone = () => {
        setAccessibleEventIds([])
    }

    const handleSave = async () => {
        if (!user) return

        setSaving(true)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/event-reallocation/user/${user.id}/events`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ event_ids: accessibleEventIds })
            })

            if (!response.ok) {
                throw new Error('Failed to save access')
            }

            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save access')
        } finally {
            setSaving(false)
        }
    }

    const handleClose = () => {
        setError(null)
        onClose()
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Manage Event Reallocation Access`}
            maxWidth="lg"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={handleClose}
                        disabled={saving}
                        variant="outline"
                        color="neutral"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {saving ? 'Saving...' : 'Save Access'}
                    </Button>
                </div>
            }
        >
            <div className="px-6 py-4">
                {user && (
                    <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>User:</strong> {user.name} ({user.email})
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Role:</strong> {user.role}
                        </p>
                    </div>
                )}

                {user?.role !== 'reallocation_manager' && (
                    <div className="mb-4 p-3 bg-warn-100 dark:bg-warn-900/30 border border-warn-200 dark:border-warn-700 rounded-lg">
                        <p className="text-sm text-warn-700 dark:text-warn-300">
                            <strong>Note:</strong> This user does not have the Reallocation Manager role.
                            Event access will only take effect when they are assigned that role.
                        </p>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-3 bg-danger-100 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-700 rounded-lg">
                        <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
                    </div>
                )}

                <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Select which events this user can reallocate volunteers to from the Duty Acceptance page.
                    </p>

                    <div className="flex gap-2 mb-3">
                        <Button variant="outline" size="sm" onClick={handleSelectAll} disabled={loading}>
                            Select All
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleSelectNone} disabled={loading}>
                            Select None
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading events...</div>
                ) : allEvents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No events found</div>
                ) : (
                    <div className="max-h-[400px] overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                        {sortByName(allEvents).map(event => {
                            const isSelected = accessibleEventIds.includes(event.id)
                            return (
                                <div
                                    key={event.id}
                                    onClick={() => handleToggleEvent(event.id)}
                                    className={`flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-600 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                        isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                    }`}
                                >
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900 dark:text-gray-100">
                                            {event.name}
                                        </p>
                                        {event.start_date && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {formatDateOnly(event.start_date)}
                                                {event.end_date && ` - ${formatDateOnly(event.end_date)}`}
                                            </p>
                                        )}
                                    </div>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                        isSelected
                                            ? 'bg-primary-500 text-white'
                                            : 'bg-gray-200 dark:bg-gray-600'
                                    }`}>
                                        {isSelected && <CheckIcon className="w-4 h-4" />}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {accessibleEventIds.length} event{accessibleEventIds.length !== 1 ? 's' : ''} selected
                </div>
            </div>
        </Modal>
    )
}
